import { yupResolver } from '@hookform/resolvers/yup';
import { FooterCustomization, IOperationResultNoValue, IRichContentEditorState } from '@ViewModels';
import { css } from 'aphrodite';
import equal from 'fast-deep-equal';
import { FC, Fragment, useEffect, useState } from 'react';
import { FieldError, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { createRichContentEditorStateWithText } from '../../../../../models/UiUtils';
import { useUpdateBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useUpdateBlogStyleMutation';
import { ColorPicker } from '../../../../../web/components-v2/ColorPicker';
import { Button } from '../../../../../web/components/Button';
import { RichContentDocumentEditor } from '../../../../../web/components/richContent/RichContentDocumentEditor';
import { PlusIcon } from '../../../../../web/components/svgs/icons/PlusIcon';
import { charmGray } from '../../../../../web/styles/colors';
import { bs } from '../../../../../web/styles/styles';
import { useAccountContext } from '../../../../components/blogs/AccountContext';
import { styleSheet } from '../../../../components/blogs/styles';
import { InfoPopover } from '../../../../components/InfoPopover/InfoPopover';
import { LabeledInput, labeledInputStyles } from '../../../../components/LabeledInput/LabeledInput';
import { Column, Row } from '../../../../components/Layout';
import { footerSchema } from './schemas';

const DEFAULT_LINK_VALUES = {
	label: '',
	destination: '',
};

const DEFAULT_BACKEND_TEXT_VALUES = {
	footerText: 'Add your disclaimer or footer text here.',
	copyrightInfo: 'Your Company Name. All rights reserved.',
};

const isDefaultFooterText = (text: string): boolean => {
	if (!text) {
		return false;
	}
	const trimmedText = text.trim();
	return (
		trimmedText === DEFAULT_BACKEND_TEXT_VALUES.footerText ||
		trimmedText === `<p>${DEFAULT_BACKEND_TEXT_VALUES.footerText}</p>` ||
		trimmedText.replace(/<[^>]*>/g, '').trim() === DEFAULT_BACKEND_TEXT_VALUES.footerText
	);
};

const DEFAULT_FORM_VALUES = {
	backgroundColor: '',
	textColor: '',
	linkColor: '',
	text: {
		document: '',
		documentVersion: 0,
		source: '',
	},
	copyrightInfo: '',
	links: new Array(3).fill(DEFAULT_LINK_VALUES),
};

interface FooterDesignFormProps {
	customContent: FooterCustomization;
	onFormDirty: () => void;
	onUpdateSuccess: () => void;
}

export const FooterDesignForm: FC<FooterDesignFormProps> = ({ customContent, onFormDirty, onUpdateSuccess }) => {
	const errorMessages = useErrorMessages();

	const {
		control,
		formState: { errors, isDirty },
		getValues,
		handleSubmit,
		register,
		setValue,
		watch,
	} = useForm<FooterCustomization>({
		defaultValues: DEFAULT_FORM_VALUES,
		mode: 'onBlur',
		resolver: yupResolver(footerSchema),
	});

	// Watch color values for live updating
	const backgroundColor = watch('backgroundColor');
	const textColor = watch('textColor');
	const linkColor = watch('linkColor');

	const { append, fields } = useFieldArray({ control, name: 'links' });
	const { accountId, impersonationContext } = useAccountContext();

	const [footerRichContentState, setFooterRichContentState] = useState<IRichContentEditorState>(
		createRichContentEditorStateWithText('')
	);
	const handleTextChange = (content: IRichContentEditorState) => {
		setValue('text', content.getRawRichTextContent());
	};

	const handleAddLink = (event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
		event.stopPropagation();

		append(DEFAULT_LINK_VALUES);
	};

	const updateFooterMutation = useUpdateBlogStyleMutation({
		onSuccess: () => {
			onUpdateSuccess();
			toast.success('Saved changes successfully');
		},
		onError: (error: IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const onSubmit: SubmitHandler<FooterCustomization> = (data, event) => {
		event.preventDefault();
		event.stopPropagation();

		updateFooterMutation.mutate({
			id: accountId,
			impersonationContext,
			style: data,
		});
	};

	useEffect(() => {
		if (customContent && !equal(customContent, getValues())) {
			Object.entries(customContent).forEach(entry => {
				if (entry[0] === 'links' && entry[1].length <= 0) {
					return;
				}
				if (entry[0] === 'links' && entry[1].length < 3) {
					// links array dynamically generates ui rows
					// minimum of 3 maintains spec
					const newLinksArray = [...entry[1]];
					newLinksArray.length = 3;
					newLinksArray.fill(DEFAULT_LINK_VALUES, entry[1].length);
					setValue('links', newLinksArray);
					return;
				}
				if (entry[0] === 'text') {
					const documentText = entry[1].document;
					if (documentText && !isDefaultFooterText(documentText)) {
						const richContentState = createRichContentEditorStateWithText(documentText);
						setFooterRichContentState(richContentState);
						setValue('text', entry[1]);
					}
					return;
				}
				if (entry[0] === 'copyrightInfo') {
					if (entry[1] && entry[1] !== DEFAULT_BACKEND_TEXT_VALUES.copyrightInfo) {
						setValue('copyrightInfo', entry[1]);
					}
					return;
				}
				if (entry[1]) {
					setValue(entry[0] as Extract<keyof FooterCustomization, string>, entry[1]);
				}
			});
		}
	}, [customContent, getValues, setValue]);

	useEffect(() => {
		if (isDirty) {
			onFormDirty();
		}
	}, [isDirty, onFormDirty]);
	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Row className={css(styleSheet.titleRow)}>
				<Row className={css(bs.flex, bs.itemsCenter, bs.gap1)}>
					<h1 className={css(styleSheet.titleText)}>Customize Footer</h1>
					<InfoPopover message='This form is applicable for both blogs and surveys.' />
				</Row>
				<input
					disabled={updateFooterMutation.isPending || !!Object.keys(errors).length}
					type='submit'
					className={css(bs.ctaButton)}
					value='Save & Update'
				/>
			</Row>
			<Row className={css(styleSheet.gap24)}>
				<Column className={css(bs.flexhalf, styleSheet.gap24)}>
					<h2 className={css(styleSheet.subtitleText)}>Colors</h2>
					<div className={css(styleSheet.gap8)}>
						<label className={css(styleSheet.label)}>Background Color</label>
						<ColorPicker value={backgroundColor || '#ffffff'} onChange={color => setValue('backgroundColor', color)} />
					</div>
					<div className={css(styleSheet.gap8)}>
						<label className={css(styleSheet.label)}>Text Color</label>
						<ColorPicker value={textColor || '#000000'} onChange={color => setValue('textColor', color)} />
					</div>
					<div className={css(styleSheet.gap8)}>
						<label className={css(styleSheet.label)}>Link Color</label>
						<ColorPicker value={linkColor || '#0066cc'} onChange={color => setValue('linkColor', color)} />
					</div>
				</Column>
				<Column className={css(bs.flex2, styleSheet.gap24)}>
					<h2 className={css(styleSheet.subtitleText)}>Information</h2>
					<Column>
						<label className={css(labeledInputStyles.label, labeledInputStyles.labelRow)}>Footer Text</label>
						<RichContentDocumentEditor
							className={css(styleSheet.editor)}
							config={{
								autoresizeToFitContent: true,
								plugins: 'autolink',
								toolbar: 'bold italic underline',
							}}
							contentPlaceholderText={DEFAULT_BACKEND_TEXT_VALUES.footerText}
							contentState={footerRichContentState}
							disableFileAttachment
							onContentStateChanged={handleTextChange}
						/>
					</Column>
					<LabeledInput
						label='Copyright Info'
						placeholder={DEFAULT_BACKEND_TEXT_VALUES.copyrightInfo}
						{...register('copyrightInfo')}
					/>
				</Column>
				<Column className={css(bs.flex1, styleSheet.gap24)}>
					<h2 className={css(styleSheet.subtitleText)}>Links</h2>
					{fields.map((field, index) => (
						<Fragment key={`fragment-${index}`}>
							<Row className={css(bs.itemsEnd, styleSheet.gap12)} key={`${field.id}-row`}>
								<LabeledInput
									key={`${field.id}-linkName`}
									label={`Link ${index + 1}`}
									placeholder='Link label'
									{...register(`links.${index}.label`)}
								/>
								<LabeledInput
									error={errors?.links?.[index] as FieldError}
									key={`${field.id}-url`}
									placeholder='URL'
									{...register(`links.${index}.destination`)}
								/>
							</Row>
						</Fragment>
					))}
					<Button accessory={<PlusIcon fillColor={charmGray} />} kind='icon' label='Add Link' onClick={handleAddLink} />
				</Column>
			</Row>
		</form>
	);
};
